import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        console.error('Failed to parse user data');
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const navItems = [
    { path: '/', label: t('nav.dashboard') },
    { path: '/bots', label: t('nav.bots') },
    { path: '/credentials', label: t('nav.exchanges') },
    { path: '/market', label: t('nav.market') },
    { path: '/macro', label: t('nav.macro') },
    { path: '/pressure', label: t('nav.pressure') },
  ];

  const mobileNavItems = [
    { path: '/', label: t('nav.dashboard'), icon: '📊' },
    { path: '/bots', label: t('nav.bots'), icon: '🤖' },
    { path: '/credentials', label: t('nav.exchanges'), icon: '🔑' },
    { path: '/market', label: t('nav.market'), icon: '📈' },
    { path: '/pressure', label: t('nav.pressure'), icon: '⚡' },
  ];

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const handleLogout = async () => {
    try {
      await fetch(getApiUrl('/auth/logout'), {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout request failed:', err);
    }
    localStorage.removeItem('user');
    setUser(null);
    setShowUserMenu(false);
    navigate('/login');
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Desktop Navbar */}
      <nav className="fixed top-0 left-0 right-0 h-14 z-sticky bg-viz-bg-primary/80 backdrop-blur-nav border-b border-viz-border">
        <div className="max-w-[1400px] mx-auto h-full flex items-center justify-between px-5">
          {/* Logo */}
          <Link to="/" className="shrink-0">
            <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              {t('common.app_name')}
            </h1>
          </Link>

          {/* Center Nav Links (hidden on mobile) */}
          <div className="hidden md:flex items-center gap-1 flex-1 justify-center">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  isActive(item.path)
                    ? 'text-viz-text-primary'
                    : 'text-viz-text-secondary hover:text-viz-text-primary hover:bg-viz-bg-tertiary/50'
                }`}
              >
                {item.label}
                {/* Active indicator line */}
                {isActive(item.path) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4/5 h-0.5 bg-viz-accent rounded-full shadow-glow" />
                )}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <select
              value={i18n.language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="bg-viz-bg-tertiary/50 border border-viz-border text-viz-text-primary rounded-md text-xs px-2 py-1.5 cursor-pointer focus:border-viz-border-active focus:outline-none transition-colors"
            >
              <option value="ru">РУ</option>
              <option value="en">EN</option>
              <option value="zh">中文</option>
              <option value="ar">عربي</option>
            </select>

            {/* User Menu / Login */}
            {user ? (
              <div className="user-menu-container relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-viz-accent/10 border border-viz-border-hover text-viz-text-primary rounded-lg text-sm font-medium transition-all duration-200 hover:bg-viz-accent/20 hover:border-viz-border-active"
                >
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-xs font-bold text-white">
                    {(user.display_name || user.email || 'U')[0].toUpperCase()}
                  </span>
                  <span className="hidden sm:inline">{user.display_name || user.email || 'User'}</span>
                  <span className={`text-[10px] transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`}>▼</span>
                </button>

                {showUserMenu && (
                  <div className="absolute top-full mt-2 right-0 bg-viz-bg-secondary border border-viz-border rounded-xl shadow-elevated min-w-[180px] p-1.5 z-dropdown">
                    <Link
                      to="/profile"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2.5 w-full px-3 py-2.5 text-viz-text-primary text-sm rounded-lg transition-colors hover:bg-viz-bg-tertiary"
                    >
                      <span>👤</span> {t('profile.title')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2.5 w-full px-3 py-2.5 text-viz-error text-sm rounded-lg transition-colors hover:bg-viz-error-bg text-left"
                    >
                      <span>🚪</span> {t('profile.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="btn-gradient text-sm !py-1.5 !px-4"
              >
                {t('auth.login')}
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 z-sticky bg-viz-bg-primary/85 backdrop-blur-nav border-t border-viz-border flex justify-around items-center px-1 pb-safe">
        {mobileNavItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex flex-col items-center justify-center gap-0.5 py-1.5 px-2.5 rounded-lg text-center min-w-[52px] transition-all duration-200 ${
              isActive(item.path)
                ? 'text-viz-accent'
                : 'text-viz-text-secondary'
            }`}
          >
            {/* Active dot indicator */}
            {isActive(item.path) && (
              <span className="absolute top-1 w-1 h-1 rounded-full bg-viz-accent shadow-glow" />
            )}
            <span className="text-lg leading-none">{item.icon}</span>
            <span className="text-[10px] leading-tight">{item.label}</span>
          </Link>
        ))}
      </nav>
    </>
  );
};

export default Navbar;
